import { Department } from "@/app/types/counter";
import { apiSlice } from "../features/api/ApiSlice";
import { AuditLog, TicketTable } from "@/app/types/tickets";
import { title } from "process";
import { service } from '@/mock/services';

export enum TicketStatus {
  NEW = "NEW",
  ARRIVED = "ARRIVED",
  CHECKED_IN = "CHECKED_IN",
  NO_SHOW = "NO_SHOW",
  IN_PROGRESS = "IN_PROGRESS",
  AWAITING_INFO = "AWAITING_INFO",
  HOLD = "HOLD",
  CANCELLED = "CANCELLED",
  COMPLETED = "COMPLETED",
  ARCHIVED = "ARCHIVED",
  RESERVED = "RESERVED",
}

export enum TicketAction {
  COMPLETE = "COMPLETE",
  IN_PROGRESS = "IN_PROGRESS",
  AWAITING_INFO = "AWAITING_INFO",
  ARCHIVED = "ARCHIVED",
  CHECKED_IN = "CHECKED_IN",
  ABSENT = "ABSENT",
  HOLD = "HOLD",
  ROUTE = "ROUTE",
  COMMENT = "COMMENT",
  UPDATE_USER = "UPDATE_USER",
  UPDATE_DETAILS = "UPDATE_DETAILS",
  LINK = "LINK",
  NO_SHOW = "NO_SHOW",
  ASSIGNED_TO = "ASSIGNED_TO",
}


export enum TicketActionOnStatus {
  COMPLETE = "COMPLETE",
  IN_PROGRESS = "IN_PROGRESS",
  AWAITING_INFO = "AWAITING_INFO",
  ARCHIVED = "ARCHIVED",
  ABSENT = "NO_SHOW",
  HOLD = "HOLD",
}

export enum CommentAction {
  ADD = "CREATE",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
}

export interface UserDetails {
  username: string;
  first_name: string;
  last_name: string;
  phone_num: string;
  email: string;
  country: number;
}
export interface Comment {
  id: number;
  ticket_id: number;
  staff_id: number;
  comment: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  staff_username: string;
}

export interface ServedBy {
  id: string;
  name: string;
}
export interface Ticket {
  title: string;
  description?: string;
  r_ticket_number: string;
  parent_ticket_id?: string;
  parent_ticket_number?: string;
  date: string;
  time: string;
  ticket_id: string;
  ticket_number: string;
  id: string;
  number: string;
  reserved_number: string;
  status: TicketStatus;
  created_at: string;
  served_at: string | null;
  eta: number;
  feedback: string;
  user_id: number;
  served_by: ServedBy;
  user_details: UserDetails;
  comments: Comment[];
  cancel_reason: string;
  parent_ticket?: Ticket | null;
  child_tickets?: Ticket[];
}

export interface Service {
  id: string;
  name: string;
}

export interface Counter {
  id: string;
  counter_code: string;
  service: Service;
  tickets: Ticket[];
}

export interface LiveQueue {
  status: TicketStatus;
  id: string;
  name: string;
  counters: Counter[];
}

export interface UpdateTicketResponse {
  success: boolean;
  message: string;
  comment: Comment;
}

interface DashboardData {
  tickets: {
    NEW: number;
    ARRIVED: number;
    CHECKED_IN: number;
    NO_SHOW: number;
    IN_PROGRESS: number;
    AWAITING_INFO: number;
    HOLD: number;
    CANCELLED: number;
    COMPLETED: number;
    ARCHIVED: number;
    RESERVED: number;
    todays_reservations: number;
  };
  tasks: {
    todo: number;
    in_progress: number;
    hold: number;
    completed: number;
    archived: number;
  };
}

export const LiveQueueApi = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      getAllUserListingOfTickets: builder.query<TicketTable, { query: string }>({
        query: ({ query }) => ({
          url: `/queue/tickets/list${query ? "?" + query : ""}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["Ticket", "LiveQueue"],
      }),
      getAllTickets: builder.query<LiveQueue[], { query: string }>({
        query: ({ query }) => ({
          url: `/kiosk/ticket/live-queue?domain=${query}&sort_key=effective_time&sort_order=asc&serving=true`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["LiveQueue"],
      }),
      getAllTicketsByDepartment: builder.query<
        LiveQueue[],
        { departmentId: number; date_filter: string }
      >({
        query: (data) => ({
          url: `/kiosk/ticket/live-queue?department_id=${data.departmentId}&sort_key=effective_time&sort_order=asc&date_filter=${data.date_filter}`,
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["LiveQueue"],
      }),
      getWaitingListTickets: builder.query<
        LiveQueue,
        {
          query: string;
        }
      >({
        query: (data) => ({
          url: `/live-queue/tickets?${data.query}`,
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["LiveQueue", "WaitingList"],
      }),
      getTicketByTicketNumber: builder.query<
        LiveQueue,
        {
          query: string;
        }
      >({
        query: (data) => ({
          url: `/live-queue/tickets?${data.query}}`,
          headers: {
            "Content-Type": "application/json",
          },
        }),
      }),
      getTicketByUserId: builder.query<
        LiveQueue,
        {
          query: string;
        }
      >({
        query: ({ query }) => ({
          url: `/live-queue/tickets?${query}`,
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["LiveQueue"],
      }),

      updateTicketStatus: builder.mutation<
        UpdateTicketResponse,
        {
          action: TicketAction;
          ticket_id: number;
          staff_id: string;
          counter_id?: number;
          service_id?: number;
          trigger_lambda: boolean;
        }
      >({
        query: ({
          action,
          ticket_id,
          staff_id,
          counter_id,
          service_id,
          trigger_lambda,
        }) => {
          return {
            url: `/live-queue/tickets/${ticket_id}/action?staff_id=${staff_id}&trigger_lambda=${trigger_lambda}`,
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: {
              action: action,
              ...(counter_id ? { counter_id } : {}),
              ...(service_id ? { service_id } : {}),
            },
          };
        },
        invalidatesTags: ["LiveQueue"],
      }),

      updateLinkedTicket: builder.mutation<
        UpdateTicketResponse,
        {
          action: TicketAction;
          ticket_id: number;
          staff_id: string;
          trigger_lambda: boolean;
          parentTicketId: number;
        }
      >({
        query: ({
          action,
          ticket_id,
          staff_id,
          trigger_lambda,
          parentTicketId,
        }) => {
          return {
            url: `/live-queue/tickets/${ticket_id}/action?staff_id=${staff_id}&trigger_lambda=${trigger_lambda}`,
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: {
              action: action,
              parent_ticket_id: parentTicketId,
            },
          };
        },
        invalidatesTags: ["LiveQueue"],
      }),
      UpdateTicketComment: builder.mutation<
        UpdateTicketResponse,
        {
          action: TicketAction;
          ticket_id: number;
          staff_id: string;
          comment_action: {
            action: CommentAction;
            comment?: string;
            comment_id?: number;
          };
        }
      >({
        query: ({ action, ticket_id, staff_id, comment_action }) => ({
          url: `/live-queue/tickets/${ticket_id}/action?staff_id=${staff_id}`,
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            action: action,
            comment_action: comment_action,
          },
        }),
      }),
      updateTitle: builder.mutation<
        UpdateTicketResponse,
        {
          action: TicketAction;
          ticketId: number;
          staff_id?: string;
          ticketData: {
            title: string;
            description: string;
          };
        }
      >({
        query: ({ action, ticketId, staff_id, ticketData }) => ({
          url: `/live-queue/tickets/${ticketId}/action${staff_id ? `?staff_id=${staff_id}` : ""}`,
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            action: action,
            title: ticketData.title,
            description: ticketData.description,
          },
        }),
      }),
      getTickeyById: builder.query<LiveQueue, { query: string }>({
        query: ({ query }) => ({
          url: `/live-queue/tickets?${query}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
      }),
      getLiveQueueTickets: builder.query<LiveQueue[], { query: string }>({
        query: ({ query }) => ({
          url: `/live-queue/${query ? "?" + query : ""}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        transformResponse: (response: Department[]): LiveQueue[] => {
          return response.map((department) => ({
            id: department.id,
            name: department.name,
            status: TicketStatus.NEW, // Default status added to fix the issue
            counters: department.counters.map((counter) => ({
              id: counter.id,
              service: counter.service,
              counter_code: (counter as any).code ?? (counter as any).counter_code ?? "",
              tickets: Array.isArray((counter as any).tickets) && (counter as any).tickets.length > 0
                ? (counter as any).tickets.map((t: any) => ({
                  r_ticket_number: t.r_ticket_number ?? t.r_ticket_number ?? "0",
                  ticket_id: t.ticket_id ?? t.ticket_number ?? "",
                  date: t.date ?? "",
                  time: t.time ?? "",
                  ticket_number: t.assigned_number ?? t.ticket_number ?? `${counter.id}-0`,
                  id: String(t.id ?? t.ticket_id ?? t.ticket_number ?? `${counter.id}-0`),
                  number: t.number ?? (t.ticket_number ? String(t.ticket_number) : ""),
                  reserved_number: t.reserved_number ?? "",
                  status: t.status ?? TicketStatus.NEW,
                  created_at: t.created_at ?? new Date().toISOString(),
                  served_at: t.served_at ?? null,
                  eta: t.eta ?? 0,
                  feedback: t.feedback ?? "",
                  user_id: t.user_id ?? 0,
                  title: t.title ?? "",
                  description: t.description ?? "",
                  user_details: t.user_details ?? {
                    username: "",
                    first_name: "",
                    last_name: "",
                    phone_num: "",
                    email: "",
                    country: 0,
                  },
                  served_by: t.served_by ?? { id: "", name: "" },
                  comments: t.comments ?? [],
                  cancel_reason: t.cancel_reason ?? "",
                }))
                : Array.from({ length: counter.number ?? 0 }, (_, index) => ({
                  r_ticket_number: "0",
                  ticket_id: "",
                  date: "",
                  time: "",
                  ticket_number: `${counter.id}-${index + 1}`,
                  id: `${counter.id}-${index + 1}`,
                  number: (index + 1).toString(),
                  reserved_number: "",
                  status: TicketStatus.NEW,
                  created_at: new Date().toISOString(),
                  served_at: null,
                  eta: 0,
                  feedback: "",
                  user_id: 0,
                  title: "",
                  description: "",
                  user_details: {
                    username: "",
                    first_name: "",
                    last_name: "",
                    phone_num: "",
                    email: "",
                    country: 0,
                  },
                  served_by: {
                    id: "",
                    name: "",
                  },
                  comments: [],
                  cancel_reason: "",
                })),
            })),
          }));
        },
        providesTags: ["LiveQueue"],
      }),
      getAllListingOfTickets: builder.query<TicketTable, { query: string }>({
        query: ({ query }) => ({
          url: `/live-queue/tickets/list${query ? "?" + query : ""}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["Ticket", "LiveQueue"],
      }),
      deleteTicket: builder.mutation<
        { message: string; deleted_count: number },
        Number[]
      >({
        query: (data) => ({
          url: `/live-queue/delete`,
          method: "DELETE",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Ticket"],
      }),
      cancelTicket: builder.mutation<
        { message: string; deleted_count: number },
        {
          ticket_ids: Number[];
          cancel_reason?: string;
          staffId?: string;
        }
      >({
        query: (data) => ({
          url: `/live-queue/cancel${data.staffId ? `?staff_id=${data.staffId}` : ""}`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Ticket"],
      }),

      getTicketFilters: builder.query<
        {
          departments: {
            id: number;
            name: string;
          }[];
          services: {
            id: number;
            name: string;
          }[];
        },
        void
      >({
        query: () => `/live-queue/filter-options`,
      }),

      getTicketAuditDetail: builder.query<AuditLog, number>({
        query: (data) => ({
          url: `/live-queue/tickets/details?ticket_id=${data}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        keepUnusedDataFor: 0,
        providesTags: ["Ticket"],
      }),

      getUserTicketDetail: builder.query<
        AuditLog,
        { ticketId: number; userId: number }
      >({
        query: (data) => ({
          url: `/live-queue/tickets/details?ticket_id=${data.ticketId}&user_id=${data.userId}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Ticket"],
      }),
      giveFeedback: builder.mutation<
        AuditLog,
        {
          ticketId: number;
          rating: number;
          waitTime: boolean;
          notes: string;
        }
      >({
        query: (data) => ({
          url: `/live-queue/tickets/${data.ticketId}/feedback?rating=${data.rating}&wait_for_long_time=${data.waitTime}&feedback=${data.notes}`,
          method: "PUT",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Ticket"],
      }),
      activetRSeries: builder.mutation<void, void>({
        query: () => ({
          url: `/live-queue/tickets/r-series/activate`,
          method: "PUT",
          headers: {
            Accept: "application/json",
          },
        }),
      }),
      getAllTaskCount: builder.query<DashboardData, void>({
        query: () => ({
          url: `/live-queue/tickets/count-by-status`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Ticket", "Tasks"],
      }),

      updateAssignedTo: builder.mutation<
        UpdateTicketResponse,
        {
          action: TicketAction;
          ticketId: number;
          staff_id?: string;
          assigned_to: string[];
        }
      >({
        query: ({ action, ticketId, staff_id, assigned_to }) => ({
          url: `/live-queue/tickets/${ticketId}/action${staff_id ? `?staff_id=${staff_id}` : ""}`,
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            action: action,
            assigned_to: assigned_to,
          },
        }),
      }),
      getServiceAvailibility: builder.query<{ [key: string]: boolean }, { service_id: number, start_date: string, end_date: string }>({
        query: ({ service_id, start_date, end_date }) => ({
          url: `/live-queue/availability/${service_id}?start_date=${start_date}&end_date=${end_date}`,
          method: "GET",
          header: {
            Accept: "application/json",
          }
        }),
        providesTags: ["Ticket"]
      })
    };
  },
});

export const {
  useGetAllUserListingOfTicketsQuery,
  useGetAllTicketsQuery,
  useGetAllTicketsByDepartmentQuery,
  useGetLiveQueueTicketsQuery,
  useGetWaitingListTicketsQuery,
  useGetTicketByTicketNumberQuery,
  useUpdateTicketStatusMutation,
  useGetTicketByUserIdQuery,
  useUpdateTicketCommentMutation,
  useLazyGetTickeyByIdQuery,
  useGetAllListingOfTicketsQuery,
  useDeleteTicketMutation,
  useCancelTicketMutation,
  useGetTicketFiltersQuery,
  useLazyGetTicketAuditDetailQuery,
  useGetUserTicketDetailQuery,
  useGiveFeedbackMutation,
  useGetTicketAuditDetailQuery,
  useActivetRSeriesMutation,
  useUpdateTitleMutation,
  useUpdateLinkedTicketMutation,
  useGetAllTaskCountQuery,
  useUpdateAssignedToMutation,
  useGetServiceAvailibilityQuery,
} = LiveQueueApi;
